// Document Basics starter copy — Feb 2026 · iter47.
//
// iter47 P0 fix: presets are no longer static strings with raw `[Company]` /
// `[X days]` / `[X years]` placeholders that contractors have to hand-edit.
// Each preset is now a template that gets resolved with the CURRENT tenant's
// company name + two structured setup fields (invoice-due days, warranty
// years) at the moment "Use starter copy" is clicked. Editor instructions
// that used to sit inside the customer-facing paragraph now live in the
// separate `editorHint` field which the UI renders as helper text below the
// textarea instead of inside it.
//
// Non-negotiables:
//   * Never hardcode a specific company name. Always resolve from the active
//     tenant's saved company_profile.company_name.
//   * If company_name is missing, DO NOT insert `[Company]` into customer-
//     facing copy. The QuickStart UI blocks the preset button and warns the
//     contractor to complete Company Basics first.
//   * Contractor edits win. `renderPreset()` returns a plain string; the
//     save handler PUTs form-state, never the template.
//   * No legal advice, no warranty guarantees, no permit / inspection
//     guarantees, no auto-send language, no customer-facing overclaims.

const _safeCompany = (companyName) => (companyName && companyName.trim())
  ? companyName.trim()
  : null;

const _safeDays = (n) => {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? String(Math.round(v)) : "15";
};

const _safeYears = (n) => {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? String(Math.round(v)) : "1";
};

// Preset definitions. `template({ company, days, years })` returns the
// fully-resolved customer-facing paragraph. No brackets, no editor
// instructions, no placeholders left over.
export const DOCUMENT_PRESETS = {
  proposal_intro: {
    label: "Starter proposal intro",
    savesTo: { endpoint: "PUT /api/document-defaults", field: "proposal_intro" },
    editorHint:
      "Rendered at the top of your proposal PDF. Edit freely — your company name is filled in automatically.",
    template: ({ company }) =>
      "Thank you for the opportunity to bid this project. The scope, materials, " +
      "and pricing below reflect what we discussed during our walkthrough. Please " +
      "review the details and let us know if anything should be adjusted before we " +
      `proceed. — ${company}`,
    // A minimum viable draft that renders even when no starter copy is
    // clicked — used by presetPreview() for the tooltip / greyed placeholder.
    minimumViablePreview:
      "Thank you for the opportunity to bid this project. …",
  },

  contract_terms: {
    label: "Starter contract terms",
    savesTo: { endpoint: "PUT /api/document-defaults", field: "contract_terms" },
    editorHint:
      "Rendered inside your contract. Replace or expand this section to match your standard contract language.",
    template: ({ company }) =>
      `This agreement covers the scope of work described in the attached proposal ` +
      `between ${company} and the customer named above. Any changes to that scope ` +
      "will be documented in writing and approved by the customer before the work " +
      "is performed. Payment terms, timeline expectations, and material selections " +
      "are as described in the proposal.",
    minimumViablePreview: "This agreement covers the scope of work described in the attached proposal…",
  },

  invoice_notes: {
    label: "Starter invoice notes",
    savesTo: { endpoint: "PUT /api/document-defaults", field: "invoice_notes" },
    editorHint:
      "Rendered on your invoice PDF. The payment window is set by the field above.",
    template: ({ company, days }) =>
      `Payment for this invoice is due within ${days} days of the invoice date. ` +
      "Please reference the invoice number when submitting payment. For questions " +
      `about billing, contact ${company} at the phone or email on this invoice.`,
    minimumViablePreview: "Payment for this invoice is due…",
  },

  workmanship_warranty_terms: {
    label: "Starter workmanship warranty",
    savesTo: { endpoint: "PUT /api/closeout-defaults", field: "workmanship_warranty_terms" },
    editorHint:
      "Rendered in your closeout / warranty packet. The warranty length is set by the field above. This is a starter template, not a legal document — adjust the coverage and terms to match your business.",
    template: ({ company, years }) =>
      `${company} warrants the workmanship of this installation for ${years} ${years === "1" ? "year" : "years"} from ` +
      "the date of substantial completion. This workmanship warranty covers " +
      "defects in installation performed by our crews. Materials are covered by " +
      "their manufacturer's warranty, which is separate from this workmanship " +
      "warranty.",
    minimumViablePreview: "Warrants the workmanship of this installation for …",
  },
};

export const DOCUMENT_PRESET_ORDER = [
  "proposal_intro",
  "contract_terms",
  "invoice_notes",
  "workmanship_warranty_terms",
];

export const DOCUMENT_PRESET_DISCLAIMER =
  "Starter template — edit for your business. Not legal advice. Your company name and the values you enter above are filled in automatically.";

// Resolve a single preset to a plain string. Returns null when the required
// company name is missing so the caller can block the click and warn instead
// of silently inserting `[Company]`.
export const renderPreset = (key, ctx = {}) => {
  const p = DOCUMENT_PRESETS[key];
  if (!p) return null;
  const company = _safeCompany(ctx.companyName);
  if (!company) return null;
  return p.template({
    company,
    days: _safeDays(ctx.invoiceDueDays),
    years: _safeYears(ctx.warrantyYears),
  });
};

// Detect unresolved bracket placeholders inside a saved value. Used by the
// Estimate Editor as a pre-flight gate before Send Proposal so a stale
// `[Company]` / `[X days]` / `[X years]` / `[Edit …]` string never lands
// in front of a customer.
//
// Kept narrow on purpose: we only flag brackets that look like our own
// placeholder tokens (uppercase word after `[`, or `[Edit …`, or `[X …`).
// Free-form contractor bracket usage (e.g. `[optional]`) is not blocked.
const PLACEHOLDER_TOKEN_RE =
  /\[(?:Company|X\s+(?:days|years)|Edit[^\]]*|Edit this[^\]]*)\]/i;

export const detectUnresolvedPlaceholders = (value) => {
  if (!value || typeof value !== "string") return false;
  return PLACEHOLDER_TOKEN_RE.test(value);
};

// Scan a bag of document-default fields and return the keys that still
// carry unresolved placeholder tokens. Returns [] when everything is clean.
export const findFieldsWithUnresolvedPlaceholders = (bag) => {
  if (!bag || typeof bag !== "object") return [];
  return Object.keys(bag).filter((k) => detectUnresolvedPlaceholders(bag[k]));
};

// ---------------------------------------------------------------------------
// Slice 2C5A · Guided Proposal wording generator.
//
// Canonical generator for the guided-intake flow. Produces the five Proposal
// fields from short answers plus the real Company Profile. Sentences that
// depend on optional profile values are OMITTED (never rendered with a
// bracket placeholder) so the Slice 2C4 placeholder validator sees clean
// text. Sentences that depend on required values return `null` for the
// whole field and the UI blocks Use-recommended with a route back to
// Business & Trades.
//
// ctx shape:
//   {
//     companyName, phone, email,
//     answers: { tone, deposit_kind, deposit_percent, deposit_amount,
//                deposit_due, final_due, final_due_custom, late_fee,
//                late_fee_amount, late_fee_custom,
//                notes_change_order, notes_warranty_separate }
//   }
//
// Returns { ok, missing, wording: { proposal_intro, proposal_footer,
//   deposit_terms, final_payment_terms, late_payment_note } } where
// `missing` lists required Company Profile keys the contractor must fill
// in first. When ok=false the UI shows a "Complete Business & Trades"
// banner and disables Use-recommended.
// ---------------------------------------------------------------------------

const _proposalIntroByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (!c) return null;
  const T = tone || "professional";
  if (T === "friendly") {
    return (
      `Thanks so much for the opportunity to earn your business — we appreciate you thinking of ${c}. ` +
      "The scope, materials, and pricing on the following pages reflect what we walked through with you. " +
      "Take a look and let us know if anything should be adjusted before we get started."
    );
  }
  if (T === "straightforward") {
    return (
      `${c} is pleased to submit this proposal for your review. ` +
      "The scope, materials, and pricing below are based on our site walkthrough. " +
      "Please review and let us know if any details need to change before we proceed."
    );
  }
  // "professional" (default)
  return (
    `Thank you for the opportunity to bid this project. The scope, materials, and pricing ` +
    `below reflect what we discussed during our walkthrough. Please review the details ` +
    `and let us know if anything should be adjusted before we proceed. — ${c}`
  );
};

const _formatMoney = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return v % 1 === 0 ? `$${v.toLocaleString()}` : `$${v.toFixed(2)}`;
};

const _depositTerms = (a) => {
  const kind = a.deposit_kind || "none";
  if (kind === "none") {
    return "No deposit is required. Payment terms are described in the final payment section below.";
  }
  const dueWords = {
    on_approval: "due when this proposal is approved",
    before_materials: "due before materials are ordered",
    before_work: "due before work begins",
  }[a.deposit_due || "on_approval"];
  if (kind === "percent") {
    const p = Number(a.deposit_percent);
    if (!Number.isFinite(p) || p <= 0 || p > 100) return null;
    return `A deposit of ${p}% of the total is ${dueWords}. The remaining balance is described below.`;
  }
  if (kind === "fixed") {
    const money = _formatMoney(a.deposit_amount);
    if (!money) return null;
    return `A deposit of ${money} is ${dueWords}. The remaining balance is described below.`;
  }
  return null;
};

const _finalPaymentTerms = (a) => {
  const k = a.final_due || "on_completion";
  if (k === "on_completion") return "The remaining balance is due when the work is complete.";
  if (k === "net_7")  return "The remaining balance is due within 7 days of substantial completion.";
  if (k === "net_15") return "The remaining balance is due within 15 days of substantial completion.";
  if (k === "net_30") return "The remaining balance is due within 30 days of substantial completion.";
  if (k === "custom") {
    const c = (a.final_due_custom || "").trim();
    return c ? `The remaining balance is due ${c}.` : null;
  }
  return null;
};

const _latePaymentNote = (a) => {
  const k = a.late_fee || "none";
  if (k === "none") return "";                      // explicit empty = no late-fee wording
  if (k === "one_point_five_percent") {
    return "Balances not paid by the due date accrue a late charge of 1.5% per month until paid in full.";
  }
  if (k === "flat") {
    const money = _formatMoney(a.late_fee_amount);
    if (!money) return null;
    return `Balances not paid by the due date accrue a flat late charge of ${money} per month until paid in full.`;
  }
  if (k === "custom") {
    const c = (a.late_fee_custom || "").trim();
    return c ? c : null;
  }
  return null;
};

const _proposalFooter = (a, company, phone, email) => {
  const c = _safeCompany(company);
  if (!c) return null;
  const lines = [];
  if (a.notes_change_order) {
    lines.push(
      "Any change to the scope of work will be documented in a written change order and " +
      "approved by the customer before that additional work is performed.",
    );
  }
  if (a.notes_warranty_separate) {
    lines.push(
      "Warranty information is provided separately at the completion of the project.",
    );
  }
  // Contact sentence is OPTIONAL: only include when at least one contact
  // channel exists. Never render a bracketed placeholder.
  const contactParts = [];
  if ((phone || "").trim()) contactParts.push(String(phone).trim());
  if ((email || "").trim()) contactParts.push(String(email).trim());
  if (contactParts.length > 0) {
    lines.push(`Questions? Contact ${c} at ${contactParts.join(" or ")}.`);
  }
  return lines.join("\n\n");
};

// --- Slice 2S · Spanish Proposal helpers -----------------------------------
const _esProposalIntroByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (!c) return null;
  const T = tone || "professional";
  if (T === "friendly") {
    return (
      `Muchas gracias por la oportunidad de ganarnos su confianza — apreciamos que haya considerado a ${c}. ` +
      "El alcance, los materiales y el precio en las siguientes páginas reflejan lo que revisamos con usted. " +
      "Por favor revíselo y avísenos si algo debe ajustarse antes de comenzar."
    );
  }
  if (T === "straightforward") {
    return (
      `${c} tiene el gusto de presentar esta propuesta para su revisión. ` +
      "El alcance, los materiales y el precio a continuación se basan en nuestra visita al sitio. " +
      "Por favor revíselo y avísenos si algún detalle debe cambiar antes de proceder."
    );
  }
  // "professional" (default)
  return (
    `Gracias por la oportunidad de cotizar este proyecto. El alcance, los materiales y el precio ` +
    `a continuación reflejan lo que conversamos durante la visita. Por favor revise los detalles ` +
    `y avísenos si algo debe ajustarse antes de proceder. — ${c}`
  );
};

const _esDepositTerms = (a) => {
  const kind = a.deposit_kind || "none";
  if (kind === "none") {
    return "No se requiere anticipo. Los términos de pago se describen en la sección de pago final a continuación.";
  }
  const dueWords = {
    on_approval: "al aprobar esta propuesta",
    before_materials: "antes de pedir los materiales",
    before_work: "antes de comenzar el trabajo",
  }[a.deposit_due || "on_approval"];
  if (kind === "percent") {
    const p = Number(a.deposit_percent);
    if (!Number.isFinite(p) || p <= 0 || p > 100) return null;
    return `Se requiere un anticipo del ${p}% del total ${dueWords}. El saldo restante se describe a continuación.`;
  }
  if (kind === "fixed") {
    const money = _formatMoney(a.deposit_amount);
    if (!money) return null;
    return `Se requiere un anticipo de ${money} ${dueWords}. El saldo restante se describe a continuación.`;
  }
  return null;
};

const _esFinalPaymentTerms = (a) => {
  const k = a.final_due || "on_completion";
  if (k === "on_completion") return "El saldo restante vence al terminar el trabajo.";
  if (k === "net_7")  return "El saldo restante vence dentro de los 7 días de la finalización sustancial.";
  if (k === "net_15") return "El saldo restante vence dentro de los 15 días de la finalización sustancial.";
  if (k === "net_30") return "El saldo restante vence dentro de los 30 días de la finalización sustancial.";
  if (k === "custom") {
    const c = (a.final_due_custom || "").trim();
    return c ? `El saldo restante vence ${c}.` : null;
  }
  return null;
};

const _esLatePaymentNote = (a) => {
  const k = a.late_fee || "none";
  if (k === "none") return "";
  if (k === "one_point_five_percent") {
    return "Los saldos no pagados en la fecha de vencimiento acumulan un cargo por mora del 1,5 % mensual hasta su pago total.";
  }
  if (k === "flat") {
    const money = _formatMoney(a.late_fee_amount);
    if (!money) return null;
    return `Los saldos no pagados en la fecha de vencimiento acumulan un cargo fijo por mora de ${money} mensual hasta su pago total.`;
  }
  if (k === "custom") {
    const c = (a.late_fee_custom || "").trim();
    return c ? c : null;
  }
  return null;
};

const _esProposalFooter = (a, company, phone, email) => {
  const c = _safeCompany(company);
  if (!c) return null;
  const lines = [];
  if (a.notes_change_order) {
    lines.push(
      "Cualquier cambio al alcance del trabajo se documentará en una orden de cambio por escrito y " +
      "será aprobado por el cliente antes de realizar ese trabajo adicional.",
    );
  }
  if (a.notes_warranty_separate) {
    lines.push(
      "La información de garantía se entrega por separado al finalizar el proyecto.",
    );
  }
  const contactParts = [];
  if ((phone || "").trim()) contactParts.push(String(phone).trim());
  if ((email || "").trim()) contactParts.push(String(email).trim());
  if (contactParts.length > 0) {
    lines.push(`¿Tiene preguntas? Contacte a ${c} al ${contactParts.join(" o ")}.`);
  }
  return lines.join("\n\n");
};

export const PROPOSAL_REQUIRED_PROFILE_KEYS = ["companyName"];

export const generateProposalWording = (ctx = {}) => {
  const answers = ctx.answers || {};
  const missing = [];
  const company = _safeCompany(ctx.companyName);
  if (!company) missing.push("companyName");
  if (missing.length > 0) return { ok: false, missing, wording: null };

  const isES = ctx.lang === "es";
  const wording = isES ? {
    proposal_intro:       _esProposalIntroByTone(answers.tone, company),
    proposal_footer:      _esProposalFooter(answers, company, ctx.phone, ctx.email),
    deposit_terms:        _esDepositTerms(answers),
    final_payment_terms:  _esFinalPaymentTerms(answers),
    late_payment_note:    _esLatePaymentNote(answers),
  } : {
    proposal_intro:       _proposalIntroByTone(answers.tone, company),
    proposal_footer:      _proposalFooter(answers, company, ctx.phone, ctx.email),
    deposit_terms:        _depositTerms(answers),
    final_payment_terms:  _finalPaymentTerms(answers),
    late_payment_note:    _latePaymentNote(answers),
  };
  // If any REQUIRED sentence returned null we cannot safely offer
  // Use-recommended. Only null (=not resolvable) counts as blocking;
  // empty string (=explicit no-content-for-this-field) is fine.
  const nulls = Object.keys(wording).filter((k) => wording[k] === null);
  if (nulls.length > 0) {
    return { ok: false, missing: ["answers:" + nulls.join(",")], wording };
  }
  return { ok: true, missing: [], wording };
};

// ---------------------------------------------------------------------------
// Help-me-change-it adjustment reducer. Applies simple local rewrites to
// wording that was ALREADY generated. No external LLM call. Returns the
// adjusted bag. The contractor still chooses Use-recommended or Edit.
// ---------------------------------------------------------------------------
export const applyHelpMeChangeIt = (wordingBag, adjustments = {}) => {
  const out = { ...(wordingBag || {}) };
  const { tone, length, add_clause, remove_clause } = adjustments;

  const softenPrefix = (s) =>
    s
      .replace(/^Thank you /i, "Thanks so much ")
      .replace(/is pleased to submit/i, "is happy to send over")
      .replace(/reflect what we discussed/i, "match what we walked through together");
  const firmPrefix = (s) =>
    s
      .replace(/^Thanks so much /i, "Thank you ")
      .replace(/is happy to send over/i, "is pleased to submit")
      .replace(/match what we walked through together/i, "reflect what we discussed");

  if (tone === "friendly" && out.proposal_intro) {
    out.proposal_intro = softenPrefix(out.proposal_intro);
  } else if (tone === "direct" && out.proposal_intro) {
    out.proposal_intro = firmPrefix(out.proposal_intro)
      .replace(/Please review the details and let us know if anything should be adjusted before we proceed\./i,
               "Please review and confirm before we proceed.");
  } else if (tone === "professional" && out.proposal_intro) {
    out.proposal_intro = firmPrefix(out.proposal_intro);
  }

  if (length === "shorter") {
    for (const k of Object.keys(out)) {
      if (typeof out[k] === "string" && out[k].length > 0) {
        // Keep only the first sentence of each field.
        const firstSentence = out[k].split(/(?<=[.!?])\s+/)[0];
        out[k] = firstSentence || out[k];
      }
    }
  } else if (length === "more_detailed") {
    if (out.proposal_intro) {
      out.proposal_intro +=
        " We take pride in clear communication throughout the project and will keep " +
        "you informed at each stage from mobilization through final walkthrough.";
    }
    if (out.final_payment_terms) {
      out.final_payment_terms +=
        " We will provide a final invoice at substantial completion for your records.";
    }
  }

  if (add_clause === "warranty_separate" && out.proposal_footer !== undefined) {
    const line = "Warranty information is provided separately at the completion of the project.";
    if (!out.proposal_footer.includes(line)) {
      out.proposal_footer = out.proposal_footer
        ? `${out.proposal_footer}\n\n${line}`
        : line;
    }
  }
  if (remove_clause === "warranty_separate" && typeof out.proposal_footer === "string") {
    out.proposal_footer = out.proposal_footer
      .split("\n\n")
      .filter((p) => !/warranty information is provided separately/i.test(p))
      .join("\n\n");
  }
  return out;
};

// ---------------------------------------------------------------------------
// Slice 2C5B · Guided Contract wording generator.
//
// Reuses saved Company Profile + project-state context. Emits three
// resolved-fields (contract_terms, contractor_disclaimer,
// customer_approval_language) covering the sixteen approved sections
// described in the Slice 2C5B PRD. No customer-facing document is ever
// sent from this generator — output is contractor-controlled and only
// becomes the saved baseline on an explicit Use-recommended save.
// ---------------------------------------------------------------------------
const _joinSections = (secs) => secs.filter(Boolean).map((s) => s.trim()).join("\n\n");

export const generateContractWording = ({
  companyName = "", phone = "", email = "",
  projectState = "",   // e.g. "CA" — reserved for future state-aware templates
  answers = {},
  lang,
} = {}) => {
  // Slice 2S · Spanish locale branch — practical, contractor-facing wording
  // that respects the same answer options. Keeps English behavior intact.
  if (lang === "es") return _generateContractWordingES({ companyName, phone, email, projectState, answers });
  const {
    tone = "professional",
    permits = "contractor",
    concealed_conditions: cc = "change_order",
    jobsite_access = [],
    scheduling = "estimated_may_change",
    material_selections = [
      "customer_approval",
      "special_order_nonreturnable",
      "substitutions_need_approval",
    ],
    cleanup = "daily_and_final",
  } = answers || {};

  const acc = new Set(jobsite_access || []);
  const mat = new Set(material_selections || []);

  // 1 · Permits & inspections ------------------------------------------------
  const permitsSection = permits === "contractor"
    ? "Permits and inspections. The Contractor will obtain and pay for the permits and inspections required for the work under this Contract."
    : permits === "customer"
      ? "Permits and inspections. The Customer will obtain and pay for the permits and inspections required for the work under this Contract."
      : permits === "not_required"
        ? "Permits and inspections. Permits are not customarily required for the work under this Contract."
        : "Permits and inspections. Responsibility for permits and inspections will be confirmed in writing for each job before work begins.";

  // 2 · Concealed / unexpected conditions -----------------------------------
  const concealedSection = cc === "change_order"
    ? "Concealed or unexpected conditions. If conditions are discovered during the work that were not visible before starting — for example concealed damage, unsafe materials, or code issues — the Contractor will stop, notify the Customer, and secure written change-order approval before performing extra work."
    : cc === "emergency_notify"
      ? "Concealed or unexpected conditions. If emergency safety work is required, the Contractor may proceed to make the jobsite safe and will notify the Customer promptly. Additional non-emergency work will require written change-order approval."
      : "Concealed or unexpected conditions. When concealed or unexpected conditions are found, the Contractor and Customer will confirm the appropriate next step in writing before extra work begins.";

  // 3 · Written change orders (always present) ------------------------------
  const changeOrderSection = "Written change orders. Any change to the scope, materials, or price of this Contract must be documented in a written change order signed by both parties before the changed work begins.";

  // 4 · Customer jobsite responsibilities -----------------------------------
  const jobsitePieces = [];
  if (acc.has("property_access"))
    jobsitePieces.push("reasonable access to the property during scheduled work hours");
  if (acc.has("utilities"))
    jobsitePieces.push("electricity and water at the jobsite");
  if (acc.has("pets"))
    jobsitePieces.push("pets kept secured away from the work area");
  if (acc.has("valuables"))
    jobsitePieces.push("removal or protection of personal valuables in the work area");
  const jobsiteSection = (acc.has("none_specific") || jobsitePieces.length === 0)
    ? "Customer jobsite responsibilities. Any customer-specific jobsite requirements will be confirmed in writing before work begins."
    : `Customer jobsite responsibilities. The Customer will provide ${jobsitePieces.join(", ")} so that the work can proceed safely and on schedule.`;

  // 5 · Scheduling ----------------------------------------------------------
  const schedulingSection = scheduling === "firm_unless_written"
    ? "Scheduling. Scheduled dates are firm unless changed in writing by both parties."
    : "Scheduling. Estimated dates may change because of weather, material availability, inspections, or other conditions outside the Contractor's control. The Contractor will keep the Customer informed of material schedule changes as they occur.";

  // 6 · Customer-caused delays / access delays ------------------------------
  const delaysSection = "Customer-caused delays. Delays caused by the Customer or by conditions the Customer controls — for example lack of access, delayed selections, or delayed approvals — may extend the schedule and, when they cause the Contractor to incur additional cost, may be addressed by a written change order.";

  // 7 · Product, color, and material selections -----------------------------
  const matPieces = [];
  if (mat.has("customer_approval"))
    matPieces.push("The Customer will approve product, color, and material selections in writing before the Contractor places orders.");
  if (mat.has("special_order_nonreturnable"))
    matPieces.push("Special-order materials may be nonreturnable once ordered.");
  if (mat.has("substitutions_need_approval"))
    matPieces.push("Comparable substitutions require the Customer's approval before installation.");
  const materialsSection = matPieces.length
    ? "Product, color, and material selections. " + matPieces.join(" ")
    : "Product, color, and material selections. Selections will be confirmed with the Customer as needed.";

  // 8 · Cleanup and debris removal ------------------------------------------
  const cleanupSection = cleanup === "daily_and_final"
    ? "Cleanup. The Contractor will provide reasonable daily cleanup and final debris removal at the end of the job."
    : cleanup === "final_only"
      ? "Cleanup. The Contractor will provide final cleanup and debris removal at the end of the job."
      : cleanup === "disposal_billed"
        ? "Cleanup. Reasonable cleanup is included; disposal will be billed separately when required by the type or volume of debris."
        : "Cleanup. Cleanup and disposal will be confirmed for each job before work begins.";

  // 9 · Code-required work / upgrades ---------------------------------------
  const codeSection = "Code-required work. Any code-required work, upgrades, or corrections discovered during the job will be addressed with the Customer through a written change order before that work is performed.";

  // 10 · Access to the jobsite ----------------------------------------------
  const accessSection = "Contractor access. The Contractor will need reasonable access to the jobsite during agreed working hours to perform the work under this Contract.";

  // 11 · Tone-driven opening sentence for the terms block -------------------
  const opener = tone === "friendly"
    ? "Thanks for choosing us for this project. Below are the terms that apply to the work."
    : tone === "straightforward"
      ? "These are the terms that apply to the work under this Contract."
      : "The following terms apply to the work performed under this Contract.";

  const contract_terms = _joinSections([
    opener,
    permitsSection,
    concealedSection,
    changeOrderSection,
    jobsiteSection,
    schedulingSection,
    delaysSection,
    materialsSection,
    cleanupSection,
    codeSection,
    accessSection,
  ]);

  // Contractor disclaimer — always the general-template disclaimer for
  // Slice 2C5B (state-specific templates land in a later slice).
  const contractor_disclaimer = _joinSections([
    "General-template disclaimer. This document is a general template supplied by JobClose AI for the Contractor's use. JobClose AI does not provide legal advice or guarantee legal compliance. The Contractor is responsible for reviewing and adapting this Contract for the specific project" + (projectState ? ` and for the State of ${projectState}` : "") + ".",
    `Contractor of record: ${companyName || "the Contractor"}${phone ? ` · ${phone}` : ""}${email ? ` · ${email}` : ""}.`,
  ]);

  // Customer approval language (kept short and readable) --------------------
  const customer_approval_language = _joinSections([
    "Customer approval. By signing below, the Customer confirms that the Customer has read and agrees to the scope, price, payment terms, and Contract terms above, including any applicable warranty information provided separately.",
    "Signatures. Contractor signature and Customer signature, with the date of signing, complete this Contract. Any change to this Contract requires a written change order signed by both parties.",
  ]);

  return { wording: { contract_terms, contractor_disclaimer, customer_approval_language } };
};

// Slice 2S · Spanish Contract generator (compact, respects same answers).
const _generateContractWordingES = ({ companyName = "", phone = "", email = "", projectState = "", answers = {} } = {}) => {
  const { tone = "professional", permits = "contractor", concealed_conditions: cc = "change_order",
    jobsite_access = [], scheduling = "estimated_may_change",
    material_selections = ["customer_approval", "special_order_nonreturnable", "substitutions_need_approval"],
    cleanup = "daily_and_final" } = answers || {};
  const acc = new Set(jobsite_access || []);
  const mat = new Set(material_selections || []);

  const permitsSection = permits === "contractor"
    ? "Permisos e inspecciones. El Contratista obtendrá y pagará los permisos e inspecciones requeridos para el trabajo bajo este Contrato."
    : permits === "customer"
      ? "Permisos e inspecciones. El Cliente obtendrá y pagará los permisos e inspecciones requeridos para el trabajo bajo este Contrato."
      : permits === "not_required"
        ? "Permisos e inspecciones. Normalmente no se requieren permisos para el trabajo bajo este Contrato."
        : "Permisos e inspecciones. La responsabilidad de los permisos e inspecciones se confirmará por escrito para cada trabajo antes de comenzar.";

  const concealedSection = cc === "change_order"
    ? "Condiciones ocultas o imprevistas. Si durante el trabajo se descubren condiciones que no eran visibles antes de comenzar — por ejemplo daños ocultos, materiales inseguros o problemas de código — el Contratista se detendrá, notificará al Cliente y obtendrá una orden de cambio aprobada por escrito antes de realizar el trabajo adicional."
    : cc === "emergency_notify"
      ? "Condiciones ocultas o imprevistas. Si se requiere trabajo de emergencia por seguridad, el Contratista puede proceder a asegurar la obra y notificará al Cliente sin demora. Cualquier trabajo adicional no urgente requerirá una orden de cambio aprobada por escrito."
      : "Condiciones ocultas o imprevistas. Cuando se detecten condiciones ocultas o imprevistas, el Contratista y el Cliente confirmarán por escrito el siguiente paso adecuado antes de realizar trabajo adicional.";

  const changeOrderSection = "Órdenes de cambio por escrito. Cualquier cambio al alcance, materiales o precio de este Contrato debe documentarse en una orden de cambio por escrito firmada por ambas partes antes de comenzar ese trabajo.";

  const jobsitePieces = [];
  if (acc.has("property_access")) jobsitePieces.push("acceso razonable a la propiedad durante las horas de trabajo programadas");
  if (acc.has("utilities")) jobsitePieces.push("electricidad y agua en la obra");
  if (acc.has("pets")) jobsitePieces.push("mascotas resguardadas lejos del área de trabajo");
  if (acc.has("valuables")) jobsitePieces.push("retiro o protección de objetos de valor personales en el área de trabajo");
  const jobsiteSection = (acc.has("none_specific") || jobsitePieces.length === 0)
    ? "Responsabilidades del Cliente en la obra. Cualquier requisito específico del Cliente se confirmará por escrito antes de comenzar el trabajo."
    : `Responsabilidades del Cliente en la obra. El Cliente proveerá ${jobsitePieces.join(", ")} para que el trabajo pueda avanzar de forma segura y a tiempo.`;

  const schedulingSection = scheduling === "firm_unless_written"
    ? "Cronograma. Las fechas programadas son firmes salvo cambio por escrito por ambas partes."
    : "Cronograma. Las fechas estimadas pueden cambiar por clima, disponibilidad de materiales, inspecciones u otras condiciones fuera del control del Contratista. El Contratista mantendrá al Cliente informado de los cambios materiales al cronograma.";

  const delaysSection = "Retrasos causados por el Cliente. Los retrasos causados por el Cliente o por condiciones que el Cliente controla — por ejemplo falta de acceso, selecciones retrasadas o aprobaciones tardías — pueden extender el cronograma y, cuando generen costos adicionales al Contratista, pueden atenderse mediante una orden de cambio por escrito.";

  const matPieces = [];
  if (mat.has("customer_approval")) matPieces.push("El Cliente aprobará por escrito los productos, colores y selecciones de materiales antes de que el Contratista realice pedidos.");
  if (mat.has("special_order_nonreturnable")) matPieces.push("Los materiales de pedido especial pueden no ser retornables una vez ordenados.");
  if (mat.has("substitutions_need_approval")) matPieces.push("Los sustitutos comparables requieren la aprobación del Cliente antes de la instalación.");
  const materialsSection = matPieces.length
    ? "Productos, colores y selecciones de materiales. " + matPieces.join(" ")
    : "Productos, colores y selecciones de materiales. Las selecciones se confirmarán con el Cliente según sea necesario.";

  const cleanupSection = cleanup === "daily_and_final"
    ? "Limpieza. El Contratista realizará limpieza diaria razonable y retiro final de escombros al terminar el trabajo."
    : cleanup === "final_only"
      ? "Limpieza. El Contratista realizará limpieza y retiro de escombros al terminar el trabajo."
      : cleanup === "disposal_billed"
        ? "Limpieza. Se incluye limpieza razonable; la disposición se facturará por separado cuando el tipo o volumen de escombros lo requiera."
        : "Limpieza. La limpieza y disposición se confirmarán por cada trabajo antes de comenzar.";

  const codeSection = "Trabajo requerido por el código. Cualquier trabajo, mejora o corrección requerido por el código descubierto durante el trabajo se atenderá con el Cliente mediante una orden de cambio por escrito antes de realizarse.";
  const accessSection = "Acceso del Contratista. El Contratista necesitará acceso razonable a la obra durante las horas de trabajo acordadas para realizar el trabajo bajo este Contrato.";

  const opener = tone === "friendly"
    ? "Gracias por elegirnos para este proyecto. A continuación se detallan los términos que aplican al trabajo."
    : tone === "straightforward"
      ? "Estos son los términos que aplican al trabajo bajo este Contrato."
      : "Los siguientes términos aplican al trabajo realizado bajo este Contrato.";

  const contract_terms = _joinSections([opener, permitsSection, concealedSection, changeOrderSection, jobsiteSection, schedulingSection, delaysSection, materialsSection, cleanupSection, codeSection, accessSection]);
  const contractor_disclaimer = _joinSections([
    "Descargo de plantilla general. Este documento es una plantilla general proporcionada por JobClose AI para uso del Contratista. JobClose AI no brinda asesoría legal ni garantiza el cumplimiento legal. El Contratista es responsable de revisar y adaptar este Contrato para el proyecto específico" + (projectState ? ` y para el Estado de ${projectState}` : "") + ".",
    `Contratista responsable: ${companyName || "el Contratista"}${phone ? ` · ${phone}` : ""}${email ? ` · ${email}` : ""}.`,
  ]);
  const customer_approval_language = _joinSections([
    "Aprobación del Cliente. Al firmar abajo, el Cliente confirma que ha leído y acepta el alcance, precio, condiciones de pago y términos del Contrato anteriores, incluida cualquier información de garantía provista por separado.",
    "Firmas. Las firmas del Contratista y del Cliente, con la fecha de firma, completan este Contrato. Cualquier cambio a este Contrato requiere una orden de cambio por escrito firmada por ambas partes.",
  ]);
  return { wording: { contract_terms, contractor_disclaimer, customer_approval_language } };
};
// optional-emphasis refinements only — tone lives in `answers.tone` and
// is applied by generateContractWording directly. Kept intentionally
// small; helper refinements never write to the server until the
// contractor explicitly clicks Use JobClose AI recommended wording.
export const applyHelpMeChangeItContract = (wordingBag, adjustments = {}) => {
  const out = { ...wordingBag };
  if (!out.contract_terms) return out;
  const paragraphs = out.contract_terms.split("\n\n");
  const emphasizeSection = (matcher, extra) => {
    for (let i = 0; i < paragraphs.length; i++) {
      if (matcher.test(paragraphs[i]) && !paragraphs[i].includes(extra)) {
        paragraphs[i] = paragraphs[i] + " " + extra;
      }
    }
  };
  const e = new Set(adjustments.emphasize || []);
  if (e.has("scheduling")) {
    emphasizeSection(/^Scheduling\./,
      "The Contractor will document material schedule changes in writing when they occur.");
  }
  if (e.has("concealed")) {
    emphasizeSection(/^Concealed or unexpected/,
      "Extra work of any kind requires a written change order before it is performed.");
  }
  if (e.has("customer_responsibility")) {
    emphasizeSection(/^Customer jobsite responsibilities\./,
      "Failure to meet these responsibilities may delay the schedule.");
  }
  if (e.has("materials")) {
    emphasizeSection(/^Product, color, and material selections\./,
      "Written approval of selections helps avoid costly reorders.");
  }
  if (e.has("cleanup")) {
    emphasizeSection(/^Cleanup\./,
      "Final walk-through will confirm cleanup and debris removal.");
  }
  out.contract_terms = paragraphs.join("\n\n");
  if (adjustments.length === "shorter") {
    // Compress by dropping the tone-driven opener (first paragraph).
    out.contract_terms = paragraphs.slice(1).join("\n\n");
  } else if (adjustments.length === "more_detailed") {
    out.contract_terms = out.contract_terms +
      "\n\nDetailed record. Communications, approvals, and material selections should be documented in writing so the record for this project is clear for both parties.";
  }
  return out;
};

// ---------------------------------------------------------------------------
// Slice 2C5B · Pass 2 · Guided Invoice wording generator.
//
// Emits the two Invoice document-default fields (invoice_notes,
// payment_instructions). The generator NEVER implies that JobClose AI is a
// payment recipient or payment processor. Payment terms may reuse the
// approved Proposal or Contract terms when the contractor selects
// `payment_due='use_approved_terms'`; Contract takes precedence when both
// are present. This generator does not build a new invoice artifact —
// downstream invoice PDFs continue to use the existing invoice display
// with the {contract amount → change orders → prior payments → current
// due → remaining balance} column preserved.
// ---------------------------------------------------------------------------

const _openerByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `Thanks for the opportunity to work with you. Here are the invoice details from ${c}.`
      : "Thanks for the opportunity to work with you. Here are your invoice details.";
  }
  if (tone === "straightforward") {
    return c
      ? `${c} · Invoice details.`
      : "Invoice details.";
  }
  return c
    ? `${c} appreciates your business. Please see the invoice details below.`
    : "Please see the invoice details below.";
};

const _billingApproachSentence = (a) => {
  const k = a.billing_approach || "one_final";
  if (k === "one_final")
    return "This invoice is issued as one final invoice for the completed work.";
  if (k === "deposit_then_final")
    return "This invoice is part of a deposit-then-final billing plan: the approved deposit is applied and any remaining balance is invoiced at completion.";
  if (k === "progress_milestone")
    return "This invoice reflects a progress or milestone billing for the work completed to date.";
  return "The billing approach for this job was determined based on the scope agreed with the customer.";
};

const _paymentDueSentence = (a, approvedProposalTerm, approvedContractTerm) => {
  const k = a.payment_due || "on_completion";
  if (k === "upon_receipt")  return "Payment is due upon receipt of this invoice.";
  if (k === "on_completion") return "Payment is due when the work invoiced is completed.";
  if (k === "net_7")         return "Payment is due within 7 days of the invoice date.";
  if (k === "net_15")        return "Payment is due within 15 days of the invoice date.";
  if (k === "net_30")        return "Payment is due within 30 days of the invoice date.";
  if (k === "use_approved_terms") {
    // Contract takes precedence when both approved terms are present.
    const reused = (approvedContractTerm || "").trim() ||
                   (approvedProposalTerm || "").trim() || "";
    if (reused) return `Payment is due per the approved terms of this job: ${reused}`;
    return "Payment is due per the approved terms of this job.";
  }
  return "Payment is due when the work invoiced is completed.";
};

const _PAYMENT_METHOD_LABELS = {
  check:     "check",
  cash:      "cash",
  card:      "credit or debit card",
  ach:       "ACH or bank transfer",
  financing: "financing",
  other:     "other arrangements confirmed in writing",
};
const _paymentMethodsSentence = (a) => {
  const methods = Array.isArray(a.payment_methods) ? a.payment_methods : [];
  if (methods.length === 0) return null;
  const labels = methods
    .map((m) => _PAYMENT_METHOD_LABELS[m])
    .filter(Boolean);
  if (labels.length === 0) return null;
  const list = labels.length === 1
    ? labels[0]
    : labels.slice(0, -1).join(", ") + " or " + labels[labels.length - 1];
  return `Accepted payment methods: ${list}.`;
};

const _cardFeeSentence = (a) => {
  const methods = Array.isArray(a.payment_methods) ? a.payment_methods : [];
  if (!methods.includes("card")) return "";     // question hidden, no sentence
  const k = a.card_fee_handling || "absorb";
  if (k === "absorb")
    return "Card processing fees are absorbed by the contractor at no additional charge to the customer.";
  if (k === "add_disclosed")
    return "A disclosed processing fee may be added when the customer chooses to pay by card, where permitted by law.";
  if (k === "per_job")
    return "Card processing fee handling is confirmed with the customer for each job.";
  return "";
};

const _overdueSentence = (a, approvedProposalTerm, approvedContractTerm) => {
  const k = a.overdue_handling || "use_approved_terms";
  if (k === "no_late_fee")
    return "No late fee is applied to overdue balances.";
  if (k === "friendly_reminder_only")
    return "Overdue balances will receive a friendly reminder rather than an automatic late fee.";
  if (k === "help_me_decide")
    return "";           // intentionally omitted from wording
  // use_approved_terms
  const reused = (approvedContractTerm || "").trim() ||
                 (approvedProposalTerm || "").trim() || "";
  if (reused) return `Overdue balances are handled per the approved late-payment terms of this job: ${reused}`;
  return "Overdue balances are handled per the approved late-payment terms of this job.";
};

const _retainageSentence = (a) => {
  if (a.billing_approach !== "progress_milestone") return "";  // hidden
  const rk = a.retainage_kind || "none";
  if (rk === "none")    return "No retainage is held on this job.";
  if (rk === "depends") return "Retainage on this job is confirmed with the customer.";
  if (rk === "percent") {
    const p = Number(a.retainage_percent);
    if (!Number.isFinite(p) || p <= 0 || p > 100) return null;
    return `Retainage of ${p}% is held pending final acceptance and released per the approved terms of this job.`;
  }
  return "";
};

const _footerLines = (a, company, phone, email, licenseInfo) => {
  const items = Array.isArray(a.footer_items) ? new Set(a.footer_items) : new Set();
  const c = _safeCompany(company);
  const lines = [];
  if (items.has("thank_customer")) {
    lines.push(c
      ? `Thank you for choosing ${c}.`
      : "Thank you for your business.");
  }
  if (items.has("contact_billing")) {
    const contactParts = [];
    if ((phone || "").trim()) contactParts.push(String(phone).trim());
    if ((email || "").trim()) contactParts.push(String(email).trim());
    if (contactParts.length > 0) {
      lines.push(`For billing questions, please contact ${c || "us"} at ${contactParts.join(" or ")}.`);
    } else {
      lines.push(`For billing questions, please contact ${c || "us"} using the details on this invoice.`);
    }
  }
  if (items.has("show_prior_payments")) {
    lines.push("Payments already received are itemized above.");
  }
  if (items.has("show_remaining_balance")) {
    lines.push("The remaining balance for this project is shown above.");
  }
  if (items.has("license_info") && (licenseInfo || "").trim()) {
    lines.push(`License: ${String(licenseInfo).trim()}`);
  }
  return lines;
};

const _paymentInstructionsBody = (a, company) => {
  const items = Array.isArray(a.footer_items) ? new Set(a.footer_items) : new Set();
  const methods = Array.isArray(a.payment_methods) ? a.payment_methods : [];
  if (!items.has("payment_instructions")) return "";
  const c = _safeCompany(company) || "the contractor";
  const lines = [];
  lines.push(`Please reference the invoice number when submitting payment to ${c}.`);
  if (methods.includes("check")) {
    lines.push("Checks may be mailed or delivered to the address on this invoice.");
  }
  if (methods.includes("ach")) {
    lines.push("ACH or bank transfer details are available on request.");
  }
  if (methods.includes("card")) {
    lines.push("Card payments are accepted using the method the contractor has on file for this job.");
  }
  if (methods.includes("financing")) {
    lines.push("Financing arrangements follow the approved financing terms for this job.");
  }
  return lines.join(" ");
};

export const generateInvoiceWording = ({
  companyName = "", phone = "", email = "",
  licenseInfo = "",
  approvedProposalPaymentTerm = "",
  approvedContractPaymentTerm = "",
  approvedProposalLateFeeTerm = "",
  approvedContractLateFeeTerm = "",
  answers = {},
  lang,
} = {}) => {
  const missing = [];
  const company = _safeCompany(companyName);
  if (!company) missing.push("companyName");
  if (missing.length > 0) return { ok: false, missing, wording: null };

  if (lang === "es") return _generateInvoiceWordingES({ company, phone, email, licenseInfo, answers,
    approvedProposalPaymentTerm, approvedContractPaymentTerm,
    approvedProposalLateFeeTerm, approvedContractLateFeeTerm });

  const opener      = _openerByTone(answers.tone, company);
  const billingLine = _billingApproachSentence(answers);
  const dueLine     = _paymentDueSentence(answers, approvedProposalPaymentTerm, approvedContractPaymentTerm);
  const methodsLine = _paymentMethodsSentence(answers);
  const cardLine    = _cardFeeSentence(answers);
  const overdueLine = _overdueSentence(answers, approvedProposalLateFeeTerm, approvedContractLateFeeTerm);
  const retainageLine = _retainageSentence(answers);
  const footerLines = _footerLines(answers, company, phone, email, licenseInfo);

  if (methodsLine === null) {
    return { ok: false, missing: ["answers:payment_methods"], wording: null };
  }
  if (retainageLine === null) {
    return { ok: false, missing: ["answers:retainage_percent"], wording: null };
  }

  const invoice_notes = [
    opener, billingLine, dueLine, methodsLine, cardLine,
    overdueLine, retainageLine,
    ...footerLines,
  ].filter((s) => typeof s === "string" && s.length > 0).join("\n\n");

  const payment_instructions = _paymentInstructionsBody(answers, company);

  return {
    ok: true, missing: [],
    wording: { invoice_notes, payment_instructions },
  };
};

// Slice 2S · Spanish Invoice generator (compact — respects same answers).
const _generateInvoiceWordingES = ({ company, phone, email, licenseInfo, answers,
  approvedProposalPaymentTerm, approvedContractPaymentTerm,
  approvedProposalLateFeeTerm, approvedContractLateFeeTerm }) => {
  const a = answers || {};
  const tone = a.tone || "professional";
  const opener = tone === "friendly"
    ? `¡Gracias por trabajar con ${company}! A continuación se detallan las condiciones de esta factura.`
    : tone === "straightforward"
      ? `Detalles de facturación de ${company}.`
      : `${company} agradece la oportunidad de trabajar en este proyecto. A continuación se detallan las condiciones de esta factura.`;
  const billing = a.billing_approach === "deposit_then_final" ? "Esta factura corresponde a un pago tras el anticipo aprobado."
    : a.billing_approach === "progress_milestone" ? "Esta es una factura por avance o hito del proyecto."
    : a.billing_approach === "depends" ? "El enfoque de facturación de este trabajo se confirma con el cliente."
    : "Esta es la factura final por el trabajo realizado.";
  const dueMap = { upon_receipt: "El pago vence al recibir esta factura.",
    on_completion: "El pago vence al terminar el trabajo.",
    net_7: "El pago vence dentro de los 7 días.", net_15: "El pago vence dentro de los 15 días.",
    net_30: "El pago vence dentro de los 30 días.",
    use_approved_terms: ((approvedContractPaymentTerm || approvedProposalPaymentTerm || "").trim()
      ? `El pago sigue los términos aprobados de este trabajo: ${(approvedContractPaymentTerm || approvedProposalPaymentTerm).trim()}`
      : "El pago sigue los términos aprobados de este trabajo.") };
  const dueLine = dueMap[a.payment_due] || dueMap.on_completion;
  const methodMap = { check: "cheque", cash: "efectivo", card: "tarjeta de crédito o débito", ach: "transferencia ACH/bancaria", financing: "financiamiento", other: "otro" };
  const pm = Array.isArray(a.payment_methods) ? a.payment_methods : [];
  const methodsLine = pm.length ? `Métodos de pago aceptados: ${pm.map((m) => methodMap[m] || m).join(", ")}.` : "";
  const cardMap = { absorb: "Absorbemos las comisiones por procesamiento con tarjeta.",
    add_disclosed: "Se agrega una comisión por procesamiento con tarjeta debidamente divulgada donde esté permitido.",
    no_cards: "No aceptamos pagos con tarjeta.", per_job: "Las comisiones por tarjeta se acuerdan por cada trabajo." };
  const cardLine = pm.includes("card") ? (cardMap[a.card_fee_handling] || "") : "";
  const overdueMap = { no_late_fee: "No se aplica recargo por mora a los saldos vencidos.",
    friendly_reminder_only: "Los saldos vencidos reciben un recordatorio amable en lugar de un recargo automático.",
    help_me_decide: "",
    use_approved_terms: ((approvedContractLateFeeTerm || approvedProposalLateFeeTerm || "").trim()
      ? `Los saldos vencidos siguen los términos de pago tardío aprobados de este trabajo: ${(approvedContractLateFeeTerm || approvedProposalLateFeeTerm).trim()}`
      : "Los saldos vencidos siguen los términos de pago tardío aprobados de este trabajo.") };
  const overdueLine = overdueMap[a.overdue_handling] || overdueMap.use_approved_terms;
  let retainageLine = "";
  if (a.billing_approach === "progress_milestone") {
    const rk = a.retainage_kind || "none";
    if (rk === "none") retainageLine = "No se retiene un porcentaje en este trabajo.";
    else if (rk === "depends") retainageLine = "La retención de este trabajo se confirma con el cliente.";
    else if (rk === "percent") {
      const p = Number(a.retainage_percent);
      if (!Number.isFinite(p) || p <= 0 || p > 100) return { ok: false, missing: ["answers:retainage_percent"], wording: null };
      retainageLine = `Se retiene el ${p}% pendiente de aceptación final y se libera según los términos aprobados de este trabajo.`;
    }
  }
  const footerItems = new Set(Array.isArray(a.footer_items) ? a.footer_items : []);
  const footer = [];
  if (footerItems.has("thank_customer")) footer.push(`Gracias por elegir a ${company}.`);
  if (footerItems.has("contact_billing")) {
    const parts = [];
    if ((phone || "").trim()) parts.push(String(phone).trim());
    if ((email || "").trim()) parts.push(String(email).trim());
    if (parts.length) footer.push(`Para consultas de facturación, contacte a ${company} al ${parts.join(" o ")}.`);
  }
  if (footerItems.has("show_prior_payments")) footer.push("Se muestran los pagos ya recibidos.");
  if (footerItems.has("show_remaining_balance")) footer.push("Se muestra claramente el saldo restante.");
  if (footerItems.has("payment_instructions")) footer.push("Vea las instrucciones de pago a continuación.");
  if (footerItems.has("license_info") && (licenseInfo || "").trim()) footer.push(`Información de licencia: ${String(licenseInfo).trim()}.`);

  const invoice_notes = [opener, billing, dueLine, methodsLine, cardLine, overdueLine, retainageLine, ...footer]
    .filter((s) => typeof s === "string" && s.length > 0).join("\n\n");
  const contactParts = [];
  if ((phone || "").trim()) contactParts.push(String(phone).trim());
  if ((email || "").trim()) contactParts.push(String(email).trim());
  const payment_instructions = contactParts.length
    ? `Envíe los pagos a ${company}. Para preguntas sobre pagos, contacte a ${company} al ${contactParts.join(" o ")}.`
    : `Envíe los pagos a ${company}.`;
  return { ok: true, missing: [], wording: { invoice_notes, payment_instructions } };
};
// optional-emphasis refinements only. Tone lives in answers.tone and is
// applied by generateInvoiceWording directly. Helper writes only to the
// local preview until Use-recommended is clicked.
export const applyHelpMeChangeItInvoice = (wordingBag, adjustments = {}) => {
  const out = { ...(wordingBag || {}) };
  if (!out.invoice_notes) return out;
  const paragraphs = out.invoice_notes.split("\n\n");
  const emphasize = new Set(adjustments.emphasize || []);
  const emphasizeSection = (matcher, extra) => {
    for (let i = 0; i < paragraphs.length; i++) {
      if (matcher.test(paragraphs[i]) && !paragraphs[i].includes(extra)) {
        paragraphs[i] = paragraphs[i] + " " + extra;
      }
    }
  };
  if (emphasize.has("amount_due")) {
    emphasizeSection(/remaining balance/i,
      "The current amount due is highlighted at the top of this invoice.");
  }
  if (emphasize.has("overdue")) {
    emphasizeSection(/overdue balances|late-payment terms|late fee/i,
      "Please contact us promptly if payment cannot be made by the due date.");
  }
  if (emphasize.has("instructions")) {
    // If payment instructions field exists, extend it too.
    if (out.payment_instructions) {
      out.payment_instructions = out.payment_instructions +
        " If you have any questions about payment methods, please reach out before the due date.";
    }
  }
  if (emphasize.has("thank_you")) {
    emphasizeSection(/Thank you|choosing/i,
      "We appreciate your continued trust in our work.");
  }
  out.invoice_notes = paragraphs.join("\n\n");
  if (adjustments.length === "shorter") {
    // Keep only opener + due sentence + first footer if present.
    const p = out.invoice_notes.split("\n\n");
    out.invoice_notes = p.slice(0, Math.min(3, p.length)).join("\n\n");
  } else if (adjustments.length === "more_detailed") {
    out.invoice_notes = out.invoice_notes +
      "\n\nA clear billing record helps both parties keep the project on schedule and avoids surprises at the end of the job.";
  }
  return out;
};

// ---------------------------------------------------------------------------
// Slice 2C5B · Pass 3 · Guided Customer Update wording generator.
//
// Emits the three Customer Update document-default fields:
//   * customer_update_signature
//   * review_request
//   * referral_request
// The generator never implies a message has been sent, never mentions
// approved photos/documents as already-shared, never leaks internal
// pricing or notes, and never references JobClose AI approval mechanics
// to the customer. Review + referral wording is respectful and optional
// (contractor still explicitly approves every send/share action).
// ---------------------------------------------------------------------------

const _cuOpenerByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `Thanks for choosing ${c} — we appreciate you.`
      : "Thanks for choosing us — we appreciate you.";
  }
  if (tone === "straightforward") {
    return c ? `— ${c}` : "";
  }
  return c
    ? `${c} appreciates your business.`
    : "Thank you for your business.";
};
const _cuOpenerByToneEs = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `Gracias por elegir a ${c}. Le agradecemos.`
      : "Gracias por elegirnos. Le agradecemos.";
  }
  if (tone === "straightforward") {
    return c ? `— ${c}` : "";
  }
  return c
    ? `${c} agradece su preferencia.`
    : "Gracias por su preferencia.";
};

const _cuContactLine = (company, phone, email) => {
  const c = _safeCompany(company) || "us";
  const parts = [];
  if ((phone || "").trim()) parts.push(String(phone).trim());
  if ((email || "").trim()) parts.push(String(email).trim());
  if (parts.length === 0) return `If you have any questions, please contact ${c}.`;
  return `If you have any questions, please contact ${c} at ${parts.join(" or ")}.`;
};
const _cuContactLineEs = (company, phone, email) => {
  const c = _safeCompany(company) || "nosotros";
  const parts = [];
  if ((phone || "").trim()) parts.push(String(phone).trim());
  if ((email || "").trim()) parts.push(String(email).trim());
  if (parts.length === 0) return `Si tiene alguna pregunta, contacte a ${c}.`;
  return `Si tiene alguna pregunta, contacte a ${c} al ${parts.join(" o ")}.`;
};

const _cuReviewByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `If ${c} did a great job, we'd love it if you shared a quick review — no pressure.`
      : "If we did a great job, we'd love it if you shared a quick review — no pressure.";
  }
  if (tone === "straightforward") {
    return "If you're satisfied with the work, an honest review helps future customers.";
  }
  return c
    ? `If you were pleased with ${c}, we would appreciate a brief review.`
    : "If you were pleased with the work, we would appreciate a brief review.";
};
const _cuReviewByToneEs = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `Si ${c} hizo un buen trabajo, nos encantaría que compartiera una reseña rápida — sin compromiso.`
      : "Si hicimos un buen trabajo, nos encantaría que compartiera una reseña rápida — sin compromiso.";
  }
  if (tone === "straightforward") {
    return "Si está satisfecho con el trabajo, una reseña honesta ayuda a futuros clientes.";
  }
  return c
    ? `Si quedó satisfecho con ${c}, apreciaríamos una breve reseña.`
    : "Si quedó satisfecho con el trabajo, apreciaríamos una breve reseña.";
};

const _cuReferralByTone = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `Know someone who might need ${c}? Referrals mean a lot to us.`
      : "Know someone who might need us? Referrals mean a lot to us.";
  }
  if (tone === "straightforward") {
    return "Referrals are welcome and appreciated.";
  }
  return c
    ? `Referrals to ${c} are always appreciated.`
    : "Referrals are always appreciated.";
};
const _cuReferralByToneEs = (tone, company) => {
  const c = _safeCompany(company);
  if (tone === "friendly") {
    return c
      ? `¿Conoce a alguien que pueda necesitar a ${c}? Sus recomendaciones significan mucho para nosotros.`
      : "¿Conoce a alguien que pueda necesitarnos? Sus recomendaciones significan mucho para nosotros.";
  }
  if (tone === "straightforward") {
    return "Sus recomendaciones son bienvenidas y apreciadas.";
  }
  return c
    ? `Las recomendaciones a ${c} siempre son apreciadas.`
    : "Las recomendaciones siempre son apreciadas.";
};

export const generateCustomerUpdateWording = ({
  companyName = "", phone = "", email = "",
  answers = {},
} = {}) => {
  const missing = [];
  const company = _safeCompany(companyName);
  if (!company) missing.push("companyName");
  if (missing.length > 0) return { ok: false, missing, wording: null };

  // Explicit-English or explicit-Spanish generate in that language.
  // Dynamic paths (`preferred`, `choose_each`) render English by default
  // with a UI-only note (rendered by the panel, not stored here).
  const lang = answers.language_rule === "es" ? "es" : "en";
  const tone = answers.tone || "professional";
  const opener = lang === "es"
    ? _cuOpenerByToneEs(tone, company)
    : _cuOpenerByTone(tone, company);
  const contact = lang === "es"
    ? _cuContactLineEs(company, phone, email)
    : _cuContactLine(company, phone, email);

  const customer_update_signature = [opener, contact]
    .filter(Boolean).join("\n\n");
  const review_request  = lang === "es"
    ? _cuReviewByToneEs(tone, company)
    : _cuReviewByTone(tone, company);
  const referral_request = lang === "es"
    ? _cuReferralByToneEs(tone, company)
    : _cuReferralByTone(tone, company);

  return {
    ok: true, missing: [],
    wording: { customer_update_signature, review_request, referral_request },
  };
};

// Slice 2C5B Pass 3 · Customer Update "Help me change it" adjustments —
// length + optional-emphasis refinements only. Writes only to the local
// preview until Use-recommended is clicked. Localized: when opts.lang
// is "es" every appended sentence is generated in professional Spanish
// so an explicit Spanish selection never produces mixed-language
// customer-facing wording.
export const applyHelpMeChangeItCustomerUpdate = (wordingBag, adjustments = {}, opts = {}) => {
  const out = { ...(wordingBag || {}) };
  const emphasize = new Set(adjustments.emphasize || []);
  const lang = opts.lang === "es" ? "es" : "en";
  // Emphasis-chip → language-specific appended sentence.
  const NEXT_STEPS = {
    en: "Next steps for this project will be shared as soon as they are confirmed.",
    es: "Los próximos pasos de este proyecto se compartirán en cuanto sean confirmados.",
  };
  const WARMER_DELAY = {
    en: "If a schedule change ever affects your project, we will let you know as soon as we can.",
    es: "Si un cambio de horario llega a afectar su proyecto, se lo haremos saber lo antes posible.",
  };
  const REVIEW_TAIL = {
    en: " Your kind words really help small local businesses.",
    es: " Sus amables palabras realmente ayudan a los pequeños negocios locales.",
  };
  const REFERRAL_TAIL = {
    en: " We treat every referral with the same care as our very first customer.",
    es: " Tratamos cada recomendación con el mismo cuidado que a nuestro primer cliente.",
  };
  const MORE_DETAILED = {
    en: "\n\nWe do our best to keep every customer clearly informed at each step of the project.",
    es: "\n\nHacemos todo lo posible por mantener a cada cliente claramente informado en cada etapa del proyecto.",
  };
  if (emphasize.has("clearer_next_steps") && out.customer_update_signature) {
    out.customer_update_signature = out.customer_update_signature +
      "\n\n" + NEXT_STEPS[lang];
  }
  if (emphasize.has("warmer_delay") && out.customer_update_signature) {
    out.customer_update_signature = out.customer_update_signature +
      "\n\n" + WARMER_DELAY[lang];
  }
  if (emphasize.has("warmer_review_referral")) {
    if (out.review_request) {
      out.review_request = out.review_request + REVIEW_TAIL[lang];
    }
    if (out.referral_request) {
      out.referral_request = out.referral_request + REFERRAL_TAIL[lang];
    }
  }
  if (emphasize.has("text_message_style") && out.customer_update_signature) {
    // Collapse to one short line usable in a text message context.
    // Language-agnostic (uses whichever signature language is present).
    const line = out.customer_update_signature.split(/\n\n|\n/).find(Boolean) || "";
    out.customer_update_signature = line;
  }
  if (adjustments.length === "shorter" && out.customer_update_signature) {
    const p = out.customer_update_signature.split("\n\n");
    out.customer_update_signature = p.slice(0, Math.min(1, p.length)).join("\n\n");
  } else if (adjustments.length === "more_detailed" && out.customer_update_signature) {
    out.customer_update_signature = out.customer_update_signature + MORE_DETAILED[lang];
  }
  return out;
};

// ---------------------------------------------------------------------------
// Slice 2C5C · Pass 1 · Guided Closeout wording generator.
// Emits only into existing Closeout document-default fields:
//   * closeout_notes_template  (main customer-facing wording)
//   * maintenance_notes         (care instructions, when applicable)
// Warranty wording itself belongs to Slice 2C5C Pass 2 — Closeout may
// reference that applicable warranty information is included, but does
// not duplicate full Warranty language.
// The generator never implies work is complete, an inspection passed,
// payment was received, documents were shared, the customer signed, or
// that JobClose AI sent/published anything.
// ---------------------------------------------------------------------------
const _coOpener = (tone, company) => {
  const c = _safeCompany(company) || "our team";
  if (tone === "friendly")        return `Thanks for choosing ${c}. Here's a summary of the closeout details for your project.`;
  if (tone === "straightforward") return `${c} · Project closeout summary.`;
  return `${c} appreciates your business. Please see the closeout summary for your project below.`;
};
const _coCompletionLine = (a) => {
  const t = a.draft_trigger || "after_inspections";
  if (t === "after_inspections")
    return "This closeout summary is prepared after work and any required inspections have been contractor-confirmed as complete.";
  if (t === "after_walkthrough")
    return "This closeout summary is prepared after the final walkthrough or customer acceptance has been contractor-confirmed.";
  if (t === "after_punchlist")
    return "This closeout summary is prepared after all punch-list items are contractor-confirmed as complete.";
  return "This closeout summary is prepared once the contractor has confirmed the appropriate completion criteria for this job.";
};
const _coWalkthroughLine = (a) => {
  const w = a.walkthrough_rule || "when_required";
  if (w === "always")        return "A final walkthrough is completed on this project.";
  if (w === "when_required") return "A final walkthrough is completed when this job requires it.";
  if (w === "none")          return "No formal walkthrough is scheduled for this project.";
  return "The walkthrough approach for this project is confirmed with the customer.";
};
const _coPunchListLine = (a) => {
  const p = a.punch_list_rule || "all_before_closeout";
  if (p === "all_before_closeout")
    return "All punch-list items are completed before closeout. Any remaining items must be contractor-confirmed before this summary is issued.";
  if (p === "written_after_ok")
    return "Any remaining punch-list items are captured in a customer-approved written punch list.";
  if (p === "none")
    return "No formal punch-list process applies to this project.";
  return "Punch-list handling for this project is confirmed with the customer.";
};
const _coAckLine = (a) => {
  const k = a.acknowledgment_rule || "written";
  if (k === "signature")   return "Customer signature is requested to acknowledge completion of this project.";
  if (k === "written")     return "Written acknowledgment from the customer is requested to confirm completion of this project.";
  if (k === "optional")    return "Customer acknowledgment of completion is optional for this project.";
  return "The customer acknowledgment approach is confirmed for this project.";
};
const _coPhotoLine = (a) => {
  const p = a.photo_rule || "final_only";
  if (p === "before_and_after")
    return "Approved before-and-after photos may be shared with the closeout summary after contractor review.";
  if (p === "final_only")
    return "Approved final photos may be shared with the closeout summary after contractor review.";
  if (p === "none")
    return "No photos are included by default in the closeout summary.";
  return "Photo handling for this project is confirmed with the customer.";
};
const _coPackageLine = (a) => {
  const items = Array.isArray(a.package_items) ? a.package_items : [];
  if (items.length === 0 || items.includes("nothing_automatic")) {
    return "Individual closeout items are provided only after contractor review and approval — nothing is attached or shared automatically.";
  }
  const labels = {
    final_invoice_summary:             "final invoice or payment summary",
    signed_contract_and_change_orders: "signed contract and approved change orders",
    workmanship_warranty:              "workmanship warranty",
    manufacturer_warranty_info:        "manufacturer warranty information",
    permit_final_inspection_docs:      "permit or final-inspection documents",
    approved_final_photos:             "approved final photos",
    care_maintenance_instructions:     "care or maintenance instructions",
  };
  const list = items.map((i) => labels[i]).filter(Boolean);
  if (list.length === 0) return "";
  const joined = list.length === 1
    ? list[0]
    : list.slice(0, -1).join(", ") + " and " + list[list.length - 1];
  return `When applicable to this project, the closeout package may include: ${joined}. Applicable warranty information (when included) is provided per the approved warranty terms.`;
};
const _coContact = (company, phone, email) => {
  const c = _safeCompany(company) || "us";
  const parts = [];
  if ((phone || "").trim()) parts.push(String(phone).trim());
  if ((email || "").trim()) parts.push(String(email).trim());
  if (parts.length === 0) return `Please contact ${c} with any final questions about this project.`;
  return `Please contact ${c} at ${parts.join(" or ")} with any final questions about this project.`;
};

export const generateCloseoutWording = ({
  companyName = "", phone = "", email = "",
  answers = {},
  lang,
} = {}) => {
  const missing = [];
  const company = _safeCompany(companyName);
  if (!company) missing.push("companyName");
  if (missing.length > 0) return { ok: false, missing, wording: null };
  if (lang === "es") return _generateCloseoutWordingES({ company, phone, email, answers });

  const tone = answers.tone || "professional";
  const parts = [
    _coOpener(tone, company),
    _coCompletionLine(answers),
    _coWalkthroughLine(answers),
    _coPunchListLine(answers),
    _coPackageLine(answers),
    _coAckLine(answers),
    _coPhotoLine(answers),
    _coContact(company, phone, email),
  ].filter(Boolean);
  const closeout_notes_template = parts.join("\n\n");

  const pkg = Array.isArray(answers.package_items) ? answers.package_items : [];
  const maintenance_notes = pkg.includes("care_maintenance_instructions")
    ? "Care and maintenance instructions applicable to this project will be provided with the closeout package after contractor review."
    : "";

  return {
    ok: true, missing: [],
    wording: { closeout_notes_template, maintenance_notes },
  };
};

// Slice 2S · Spanish Closeout generator.
const _generateCloseoutWordingES = ({ company, phone, email, answers }) => {
  const a = answers || {};
  const tone = a.tone || "professional";
  const opener = tone === "friendly"
    ? `¡Gracias por trabajar con ${company}! A continuación se detalla el cierre del proyecto.`
    : tone === "straightforward"
      ? `${company} — Cierre del proyecto.`
      : `${company} presenta este resumen de cierre para su revisión.`;
  const completionMap = { after_inspections: "Este cierre se prepara después de confirmar el trabajo y las inspecciones requeridas.",
    after_walkthrough: "Este cierre se prepara después de la revisión final o aceptación del cliente.",
    after_punchlist: "Este cierre se prepara después de completar los pendientes del punch list.",
    decide_each: "El momento del cierre se confirma con el cliente para este trabajo." };
  const walkthroughMap = { always: "Se realizó una revisión final del trabajo con el cliente.",
    when_required: "Se realizó una revisión final cuando el trabajo lo requirió.",
    none: "No se realizó una revisión formal en este trabajo.",
    decide_each: "La revisión final se acordó por este trabajo." };
  const punchMap = { all_before_closeout: "Todos los pendientes del punch list se completaron antes del cierre.",
    written_after_ok: "Cualquier pendiente restante está documentado por escrito y aprobado por el cliente.",
    none: "No se usa un proceso formal de punch list en este trabajo.",
    decide_each: "Los pendientes de este trabajo se acordaron por escrito con el cliente." };
  const pkgMap = { final_invoice_summary: "resumen final de factura o pago",
    signed_contract_and_change_orders: "contrato firmado y órdenes de cambio aprobadas",
    workmanship_warranty: "garantía de mano de obra",
    manufacturer_warranty_info: "información de garantía del fabricante",
    permit_final_inspection_docs: "documentos de permiso o inspección final",
    approved_final_photos: "fotos finales aprobadas",
    care_maintenance_instructions: "instrucciones de cuidado o mantenimiento" };
  const pkg = Array.isArray(a.package_items) ? a.package_items.filter((x) => x !== "nothing_automatic") : [];
  const pkgLine = pkg.length ? `El paquete de cierre incluye: ${pkg.map((k) => pkgMap[k] || k).join(", ")}.` : "";
  const ackMap = { signature: "El cliente confirma la finalización con su firma.",
    written: "El cliente confirma la finalización mediante reconocimiento por escrito.",
    optional: "El reconocimiento de finalización por el cliente es opcional.",
    decide_each: "El reconocimiento de finalización se acordó por este trabajo." };
  const photoMap = { before_and_after: "Se incluyen fotos aprobadas de antes y después.",
    final_only: "Se incluyen solo las fotos finales aprobadas.",
    none: "No se incluyen fotos por defecto.",
    decide_each: "Las fotos incluidas se acordaron por este trabajo." };
  const parts = [
    opener,
    completionMap[a.draft_trigger] || completionMap.after_inspections,
    walkthroughMap[a.walkthrough_rule] || walkthroughMap.when_required,
    punchMap[a.punch_list_rule] || punchMap.all_before_closeout,
    pkgLine,
    ackMap[a.acknowledgment_rule] || ackMap.written,
    photoMap[a.photo_rule] || photoMap.final_only,
  ].filter(Boolean);
  const contactParts = [];
  if ((phone || "").trim()) contactParts.push(String(phone).trim());
  if ((email || "").trim()) contactParts.push(String(email).trim());
  if (contactParts.length) parts.push(`Para preguntas finales sobre este proyecto, contacte a ${company} al ${contactParts.join(" o ")}.`);
  else parts.push(`Contacte a ${company} para preguntas finales sobre este proyecto.`);
  const closeout_notes_template = parts.join("\n\n");
  const maintenance_notes = pkg.includes("care_maintenance_instructions")
    ? "Se entregarán instrucciones de cuidado y mantenimiento aplicables a este proyecto con el paquete de cierre tras la revisión del contratista."
    : "";
  return { ok: true, missing: [], wording: { closeout_notes_template, maintenance_notes } };
};

export const applyHelpMeChangeItCloseout = (wordingBag, adjustments = {}) => {
  const out = { ...(wordingBag || {}) };
  if (!out.closeout_notes_template) return out;
  const emphasize = new Set(adjustments.emphasize || []);
  if (emphasize.has("clearer_final_next_steps")) {
    out.closeout_notes_template = out.closeout_notes_template +
      "\n\nFinal next steps for this project will be shared as soon as they are confirmed.";
  }
  if (emphasize.has("stronger_punch_list_clarity")) {
    out.closeout_notes_template = out.closeout_notes_template +
      "\n\nAny remaining punch-list items are tracked in writing and will be closed out only after contractor confirmation.";
  }
  if (emphasize.has("warmer_completion")) {
    out.closeout_notes_template = out.closeout_notes_template +
      "\n\nWe appreciate your trust throughout this project.";
  }
  if (emphasize.has("clearer_ack")) {
    out.closeout_notes_template = out.closeout_notes_template +
      "\n\nYour acknowledgment of completion helps us confirm the record for this project.";
  }
  if (adjustments.length === "shorter") {
    const p = out.closeout_notes_template.split("\n\n");
    out.closeout_notes_template = p.slice(0, Math.min(3, p.length)).join("\n\n");
  } else if (adjustments.length === "more_detailed") {
    out.closeout_notes_template = out.closeout_notes_template +
      "\n\nA clear closeout record helps keep both parties aligned on the finished scope of work and any remaining items.";
  }
  return out;
};

// ---------------------------------------------------------------------------
// Slice 2C5C · Pass 2 · Warranty wording generator + Help me change it.
// Writes only into the three existing warranty document-default fields:
// workmanship_warranty_terms, manufacturer_warranty_notes, warranty_contact_notes.
// Never guarantees manufacturer coverage. Never issues, activates, or
// sends a warranty. Never claims a concern is automatically covered.
// ---------------------------------------------------------------------------
const _wOpener = (tone, companyName) => {
  const c = companyName || "This company";
  if (tone === "friendly") {
    return `${c} appreciates your business. This section explains the standard warranty preferences that apply to future project documents.`;
  }
  if (tone === "straightforward") {
    return `${c} provides the following standard warranty terms on future project documents.`;
  }
  return `${c} provides the following standard warranty terms on future project documents.`;
};

const _wPeriodPhrase = (period, custom) => {
  if (period === "custom" && custom) return String(custom).trim();
  const map = {
    "30_days":  "thirty (30) days",
    "90_days":  "ninety (90) days",
    "1_year":   "one (1) year",
    "2_years":  "two (2) years",
    "5_years":  "five (5) years",
  };
  return map[period] || "one (1) year";
};

const _wCoverageStartPhrase = (start) => {
  if (start === "contractor_substantial")
    return "the date of contractor-confirmed substantial completion for this project";
  if (start === "customer_acknowledgment")
    return "the date of customer acknowledgment of completion";
  if (start === "decide_each")
    return "the coverage start date identified in the project-specific documents";
  return "the date of contractor-confirmed final completion for this project";
};

const _wCoverageLines = (arr) => {
  const items = [];
  if ((arr || []).includes("defects_workmanship")) {
    items.push("Defects caused by our workmanship on the covered work.");
  }
  if ((arr || []).includes("repairs_covered_work")) {
    items.push("Repairs to the covered work when a valid workmanship issue is confirmed after contractor review.");
  }
  if ((arr || []).includes("decide_each")) {
    items.push("Specific covered items will be identified in the project-specific documents.");
  }
  return items;
};

const _wExclusionLines = (arr) => {
  const map = {
    manufacturer_or_product_defects:  "Manufacturer or product defects (see the manufacturer warranty section).",
    normal_wear_and_tear:             "Normal wear and tear.",
    misuse_abuse_neglect:             "Damage caused by misuse, abuse, neglect, or lack of maintenance.",
    weather_disasters_outside_forces: "Damage caused by weather, disasters, or other outside forces.",
    altered_or_repaired_by_others:    "Work altered or repaired by others.",
    pre_existing_or_out_of_scope:     "Pre-existing conditions or work outside the approved project scope.",
    decide_each:                      "Specific exclusions will be identified in the project-specific documents.",
  };
  return (arr || []).map((k) => map[k]).filter(Boolean);
};

const _wTradeOverridesLine = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  if (list.length === 0) return "";
  const parts = list.map((o) => {
    const p = _wPeriodPhrase(o.period, o.custom_period);
    return `${o.trade}: ${p}`;
  });
  return `Some trades follow a different workmanship warranty period. Applicable trade override: ${parts.join("; ")}.`;
};

const _wLegalTail = () =>
  "Nothing in this section waives any rights required by applicable law.";

const _wContractLine = (companyName, phone, email) => {
  const parts = [];
  parts.push(companyName || "our company");
  if (phone) parts.push(String(phone));
  if (email) parts.push(String(email));
  return `To request warranty service, contact ${parts.join(" · ")}.`;
};

export const generateWarrantyWording = ({
  companyName = "", phone = "", email = "",
  answers = {}, activeTrades = [], // eslint-disable-line no-unused-vars
  lang,
} = {}) => {
  if (lang === "es") return _generateWarrantyWordingES({ companyName, phone, email, answers });
  const co = companyName || "This company";
  const tone = answers.tone || "professional";
  const wo   = answers.workmanship_offered || "yes";
  const wp   = answers.workmanship_period;
  const cwp  = answers.custom_workmanship_period;
  const cs   = answers.coverage_start;
  const cov  = answers.workmanship_coverage || [];
  const exc  = answers.workmanship_exclusions || [];
  const mfg  = answers.manufacturer_handling || "provide_available";
  const scr  = answers.service_contact_rule || "company_contact";
  const ssc  = answers.specific_service_contact;
  const ri   = answers.request_information || [];
  const useOverrides = answers.use_trade_overrides === true;
  const overrides = useOverrides ? (answers.trade_overrides || []) : [];

  // Workmanship warranty terms.
  const paras = [_wOpener(tone, co)];
  if (wo === "yes") {
    const per = _wPeriodPhrase(wp, cwp);
    paras.push(
      `Workmanship warranty period: ${per}, beginning on ${_wCoverageStartPhrase(cs)}.`,
    );
    const cLines = _wCoverageLines(cov);
    if (cLines.length) {
      paras.push("Covered workmanship items include:\n- " + cLines.join("\n- "));
    }
    const eLines = _wExclusionLines(exc);
    if (eLines.length) {
      paras.push("The workmanship warranty does not cover:\n- " + eLines.join("\n- "));
    }
    paras.push(
      "Any concern must be reviewed by the contractor before a repair or remedy is determined. A repair or remedy applies only when a valid covered workmanship issue is confirmed after review.",
    );
    const to = _wTradeOverridesLine(overrides);
    if (to) paras.push(to);
  } else if (wo === "no_default") {
    paras.push(
      "No standard workmanship warranty is included by default. A workmanship warranty applies only when it is specifically written into the project-specific documents. This does not affect any applicable manufacturer or product warranty.",
    );
  } else {
    // decide_each
    paras.push(
      "Any workmanship warranty for this project will be identified in the project-specific documents.",
    );
  }
  paras.push(_wLegalTail());
  const workmanship_warranty_terms = paras.join("\n\n");

  // Manufacturer warranty notes.
  const mLines = [];
  if (mfg === "provide_available") {
    mLines.push(
      "Manufacturer or product warranty coverage is separate from the contractor workmanship warranty above. When manufacturer warranty information is available for materials or products used on this project, we will provide it with the closeout documents.",
    );
  } else if (mfg === "refer_to_manufacturer") {
    mLines.push(
      "Manufacturer or product warranty coverage is separate from the contractor workmanship warranty above. For manufacturer warranty questions, please contact the applicable product manufacturer directly.",
    );
  } else if (mfg === "none_default") {
    mLines.push(
      "Manufacturer or product warranty information is not included by default. Manufacturer coverage is separate from any contractor workmanship warranty and is governed by the applicable manufacturer terms.",
    );
  } else {
    // decide_each
    mLines.push(
      "Manufacturer or product warranty information for this project will be identified in the project-specific documents. Manufacturer coverage is separate from any contractor workmanship warranty.",
    );
  }
  mLines.push(
    "Manufacturer coverage, duration, registration requirements, approval, and claim decisions are controlled by the applicable manufacturer, not by this company.",
  );
  const manufacturer_warranty_notes = mLines.join("\n\n");

  // Warranty contact notes.
  const contactLines = [];
  if (scr === "specific" && ssc && String(ssc).trim()) {
    contactLines.push(`To request warranty service, contact: ${String(ssc).trim()}.`);
  } else if (scr === "decide_each") {
    contactLines.push(
      "Warranty service contact information for this project will be identified in the project-specific documents.",
    );
  } else {
    contactLines.push(_wContractLine(co, phone, email));
  }
  const infoMap = {
    customer_and_project_name: "Customer and project name.",
    description_of_concern:    "A description of the concern.",
    photos_when_available:     "Photos of the concern when available.",
    date_first_noticed:        "The date the concern was first noticed.",
    decide_each:               "Specific request information will be identified in the project-specific documents.",
  };
  const infoLines = (ri || []).map((k) => infoMap[k]).filter(Boolean);
  if (infoLines.length) {
    contactLines.push(
      "When requesting warranty service, please provide:\n- " + infoLines.join("\n- "),
    );
  }
  contactLines.push(
    "Submitting a warranty request does not automatically mean the concern is covered. Every concern is reviewed by the contractor before a repair or remedy is determined.",
  );
  const warranty_contact_notes = contactLines.join("\n\n");

  const missingCompany = !companyName;
  return {
    ok: !missingCompany,
    reason: missingCompany ? "missing_company_name" : "",
    wording: {
      workmanship_warranty_terms,
      manufacturer_warranty_notes,
      warranty_contact_notes,
    },
  };
};

// Slice 2S · Spanish Warranty generator.
const _generateWarrantyWordingES = ({ companyName, phone, email, answers }) => {
  const co = companyName || "Esta empresa";
  const a = answers || {};
  const tone = a.tone || "professional";
  const wo = a.workmanship_offered || "yes";
  const opener = tone === "friendly"
    ? `${co} respalda su trabajo y quiere que usted sepa qué esperar si surge una inquietud.`
    : tone === "straightforward"
      ? `Cobertura de garantía de ${co}.`
      : `${co} presenta las siguientes condiciones de garantía para su revisión.`;
  const wPeriodMap = { "30_days": "30 días", "90_days": "90 días", "1_year": "1 año", "2_years": "2 años", "5_years": "5 años", custom: (a.custom_workmanship_period || "período personalizado"), decide_each: "período confirmado por cada trabajo" };
  const csMap = { contractor_substantial: "la finalización sustancial confirmada por el contratista",
    contractor_final: "la finalización final confirmada por el contratista",
    customer_acknowledgment: "el reconocimiento de finalización por el cliente",
    decide_each: "la fecha confirmada por cada trabajo" };
  const covMap = { defects_workmanship: "defectos causados por nuestra mano de obra",
    repairs_covered_work: "reparaciones del trabajo cubierto cuando se confirme un problema válido de mano de obra",
    decide_each: "cobertura decidida por cada trabajo" };
  const excMap = { manufacturer_or_product_defects: "defectos del fabricante o del producto",
    normal_wear_and_tear: "desgaste normal por uso",
    misuse_abuse_neglect: "daños por mal uso, abuso, negligencia o falta de mantenimiento",
    weather_disasters_outside_forces: "daños por clima, desastres o fuerzas externas",
    altered_or_repaired_by_others: "trabajos alterados o reparados por otros",
    pre_existing_or_out_of_scope: "condiciones preexistentes o trabajo fuera del alcance aprobado",
    decide_each: "exclusiones decididas por cada trabajo" };

  const paras = [opener];
  if (wo === "yes") {
    paras.push(`Período de garantía de mano de obra: ${wPeriodMap[a.workmanship_period] || wPeriodMap["1_year"]}, a partir de ${csMap[a.coverage_start] || csMap.contractor_final}.`);
    const cov = (a.workmanship_coverage || []).map((k) => covMap[k]).filter(Boolean);
    if (cov.length) paras.push("La cobertura de mano de obra incluye:\n- " + cov.join("\n- "));
    const exc = (a.workmanship_exclusions || []).map((k) => excMap[k]).filter(Boolean);
    if (exc.length) paras.push("La garantía de mano de obra no cubre:\n- " + exc.join("\n- "));
    paras.push("Cualquier inquietud debe ser revisada por el contratista antes de determinar una reparación. La reparación aplica solo cuando se confirma un problema válido de mano de obra tras la revisión.");
  } else if (wo === "no_default") {
    paras.push("Por defecto no se incluye una garantía estándar de mano de obra. Cualquier garantía aplicará solo cuando esté específicamente escrita en los documentos del proyecto. Esto no afecta las garantías del fabricante o del producto.");
  } else {
    paras.push("Cualquier garantía de mano de obra para este proyecto se identificará en los documentos específicos del proyecto.");
  }
  paras.push("Esta información es una guía general provista por JobClose AI. La cobertura, exclusiones y remedios reales se rigen por los términos escritos en los documentos del proyecto.");
  const workmanship_warranty_terms = paras.join("\n\n");

  const mLines = [];
  const mfg = a.manufacturer_handling || "provide_available";
  if (mfg === "provide_available") mLines.push("Cuando corresponda, se proporcionará la información de garantía del fabricante disponible. Las garantías del fabricante se rigen exclusivamente por sus propios términos escritos y no están garantizadas por el contratista.");
  else if (mfg === "refer_to_manufacturer") mLines.push("Las consultas sobre productos o materiales se dirigen directamente al fabricante correspondiente. Las garantías del fabricante se rigen exclusivamente por sus propios términos escritos.");
  else if (mfg === "none_default") mLines.push("Por defecto no se incluye información de garantía del fabricante. Cualquier cobertura del fabricante se rige exclusivamente por los términos escritos del fabricante.");
  else mLines.push("El manejo de la garantía del fabricante se confirma por cada trabajo.");
  const manufacturer_warranty_notes = mLines.join("\n\n");

  const scr = a.service_contact_rule || "company_contact";
  const contactParts = [];
  if ((phone || "").trim()) contactParts.push(String(phone).trim());
  if ((email || "").trim()) contactParts.push(String(email).trim());
  const contactLines = [];
  if (scr === "company_contact") {
    contactLines.push(contactParts.length
      ? `Para solicitar servicio de garantía, contacte a ${co} al ${contactParts.join(" o ")}.`
      : `Para solicitar servicio de garantía, contacte a ${co}.`);
  } else if (scr === "specific") {
    contactLines.push(`Contacto específico de garantía: ${(a.specific_service_contact || "").trim() || "por confirmar"}.`);
  } else {
    contactLines.push("El contacto de garantía se confirma por cada trabajo.");
  }
  const ri = a.request_information || [];
  if (ri.length) {
    const infoMap = { customer_and_project_name: "nombre del cliente y del proyecto",
      description_of_concern: "descripción de la inquietud",
      photos_when_available: "fotos de la inquietud cuando estén disponibles",
      date_first_noticed: "fecha en que se notó por primera vez",
      decide_each: "información confirmada por cada trabajo" };
    contactLines.push("Al solicitar el servicio, por favor proporcione:\n- " + ri.map((k) => infoMap[k] || k).join("\n- "));
  }
  const warranty_contact_notes = contactLines.join("\n\n");
  const missingCompany = !companyName;
  return {
    ok: !missingCompany,
    reason: missingCompany ? "missing_company_name" : "",
    wording: { workmanship_warranty_terms, manufacturer_warranty_notes, warranty_contact_notes },
  };
};

export const applyHelpMeChangeItWarranty = (wordingBag, adjustments = {}) => {
  const out = { ...(wordingBag || {}) };
  const emphasize = new Set((adjustments.emphasize || []).filter(Boolean));
  if (emphasize.has("clearer_workmanship_coverage") && out.workmanship_warranty_terms) {
    out.workmanship_warranty_terms = out.workmanship_warranty_terms +
      "\n\nCovered workmanship items are limited to defects caused by our workmanship on the approved scope of work, confirmed after contractor review.";
  }
  if (emphasize.has("stronger_exclusion_clarity") && out.workmanship_warranty_terms) {
    out.workmanship_warranty_terms = out.workmanship_warranty_terms +
      "\n\nThe exclusions above apply in addition to any exclusions required by applicable law.";
  }
  if (emphasize.has("clearer_manufacturer_distinction") && out.manufacturer_warranty_notes) {
    out.manufacturer_warranty_notes = out.manufacturer_warranty_notes +
      "\n\nContractor workmanship coverage and manufacturer or product coverage are two separate warranties governed by different terms.";
  }
  if (emphasize.has("clearer_warranty_request_steps") && out.warranty_contact_notes) {
    out.warranty_contact_notes = out.warranty_contact_notes +
      "\n\nProviding the requested information helps us review the concern promptly and clearly.";
  }
  if (adjustments.length === "shorter") {
    for (const k of ["workmanship_warranty_terms", "manufacturer_warranty_notes", "warranty_contact_notes"]) {
      if (out[k]) {
        const p = out[k].split("\n\n");
        out[k] = p.slice(0, Math.min(3, p.length)).join("\n\n");
      }
    }
  } else if (adjustments.length === "more_detailed") {
    if (out.workmanship_warranty_terms) {
      out.workmanship_warranty_terms = out.workmanship_warranty_terms +
        "\n\nA clear warranty record helps keep both parties aligned on what is and is not covered before, during, and after the project.";
    }
  }
  return out;
};

